import React, { useEffect, useState } from 'react';
import { Shield, CheckCircle, Database, Search, Clock, Lock } from 'lucide-react';

const CriminalBackgroundCheck = () => {
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fadeIn');
            entry.target.classList.remove('opacity-0');
          }
        });
      },
      { threshold: 0.1 }
    );

    const hiddenElements = document.querySelectorAll('.initially-hidden');
    hiddenElements.forEach((el) => observer.observe(el));

    const interval = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % 3);
    }, 3000);

    return () => {
      hiddenElements.forEach((el) => observer.unobserve(el));
      clearInterval(interval);
    };
  }, []);

  const processSteps = [
    {
      title: "AI-Powered Identity Verification",
      description: "Artificial intelligence technology is employed to detect fraudulent documents",
      icon: <Database className="w-8 h-8 text-red-300" />,
      image: "https://images.pexels.com/photos/3183150/pexels-photo-3183150.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1"
    },
    {
      title: "National Criminal Background Check",
      description: "The verification process includes a search of criminal records throughout India",
      icon: <Search className="w-8 h-8 text-red-300" />,
      image: "https://images.pexels.com/photos/607812/pexels-photo-607812.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1"
    },
    {
      title: "Effective Verification",
      description: "Results are delivered within 24 to 48 hours",
      icon: <Clock className="w-8 h-8 text-red-300" />,
      image: "https://images.pexels.com/photos/590016/pexels-photo-590016.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-950 to-gray-900 text-white">
      {/* Hero Section */}
      <section className="relative h-[60vh] overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-red-900/60 to-red-900/90 z-10"></div>
        
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1589578527966-fdac0f44566c?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80" 
            alt="Criminal Background Check" 
            className="w-full h-full object-cover"
          />
        </div>
        
        <div className="relative z-20 container mx-auto px-4 h-full flex flex-col justify-center">
          <div className="max-w-3xl">
            <div className="inline-block mb-4">
              <div className="bg-white/10 p-3 rounded-full animate-pulse">
                <Shield className="h-8 w-8 text-red-300" />
              </div>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-4 animate-fadeInDown">
              Criminal Background Check
            </h1>
            <p className="text-xl text-red-200 mb-8 max-w-2xl animate-fadeInUp">
              Assessing a person's past criminal history during hiring to identify risks and ensure workplace safety.
            </p>
            <div className="flex flex-wrap gap-4 animate-fadeInUp">
              <button className="bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-6 rounded-lg transition-all duration-300 transform hover:scale-105 flex items-center">
                <Lock className="w-5 h-5 mr-2" />
                Learn More
              </button>
              {/* <button className="bg-transparent border-2 border-red-400 hover:bg-red-400/20 text-white font-bold py-3 px-6 rounded-lg transition-all duration-300">
                Watch Demo
              </button> */}
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="container mx-auto px-4 py-16">
        <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-8 shadow-2xl border border-white/20 mb-16 initially-hidden opacity-0">
          <h2 className="text-3xl font-bold mb-8 text-center">Understanding Criminal Background Checks</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            <div>
              <p className="text-lg leading-relaxed mb-6">
                Criminal background checks assess a person's past criminal history, usually conducted during the hiring process, to identify potential risks and ensure the safety of the workplace.
              </p>
              <p className="text-lg leading-relaxed">
                This process is critical for maintaining a secure environment and making informed hiring decisions.
              </p>
              
              <div className="mt-8 bg-red-900/30 p-6 rounded-lg border border-red-500/30">
                <h3 className="text-xl font-bold mb-4 flex items-center">
                  <Shield className="w-6 h-6 text-red-300 mr-2" />
                  Why It Matters
                </h3>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <span className="text-red-300 mr-2">✓</span>
                    <span>Create a Safe Working Space: Protects employees, customers, and workplace</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-300 mr-2">✓</span>
                    <span>Maintain Organization’s Reputation: Preserves brand image and customer trust</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-300 mr-2">✓</span>
                    <span>Make Fair Hiring Decisions: Enables balanced and informed choices</span>
                  </li>
                </ul>
              </div>
            </div>
            <div className="relative rounded-xl overflow-hidden h-full shadow-lg">
              <img 
                src="https://images.pexels.com/photos/3183150/pexels-photo-3183150.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1" 
                alt="Background Check" 
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-red-900/80 to-transparent flex items-end">
                <p className="p-4 text-white font-medium">Ensuring workplace safety through thorough screening</p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Verification Process Animation */}
        <div className="mb-16 initially-hidden opacity-0">
          <h2 className="text-3xl font-bold mb-8 text-center">The Verification Process</h2>
          
          <div className="relative">
            <div className="hidden md:block absolute top-0 left-1/2 w-1 bg-red-500/50 h-full transform -translate-x-1/2 z-0"></div>
            
            {processSteps.map((step, index) => (
              <div 
                key={index} 
                className={`relative z-10 transition-all duration-500 mb-8 ${
                  activeStep === index ? 'scale-100' : 'scale-95 opacity-70'
                }`}
              >
                <div className="flex flex-col md:flex-row items-center">
                  <div className={`w-full md:w-1/2 ${index % 2 === 0 ? 'md:pr-12 md:text-right' : 'md:order-2 md:pl-12'}`}>
                    <div className={`bg-white/5 p-6 rounded-lg border border-white/10 transition-all duration-300 ${
                      activeStep === index ? 'bg-white/15 border-red-500/50 shadow-lg' : ''
                    }`}>
                      <div className="flex items-center mb-4 justify-center">
                        <div className={`p-3 rounded-full ${
                          activeStep === index ? 'bg-red-600/30' : 'bg-white/10'
                        }`}>
                          {step.icon}
                        </div>
                      </div>
                      <h3 className="text-xl font-bold text-red-300 mb-2">{step.title}</h3>
                      <p className="text-white/80">{step.description}</p>
                    </div>
                  </div>
                  
                  <div className="absolute left-1/2 transform -translate-x-1/2 hidden md:flex items-center justify-center">
                    <div className={`w-10 h-10 rounded-full border-4 z-20 flex items-center justify-center transition-all duration-300 ${
                      activeStep === index 
                        ? 'bg-red-600 border-red-900 scale-125' 
                        : 'bg-red-900/70 border-red-700/50'
                    }`}>
                      <span className="text-white font-bold">{index + 1}</span>
                    </div>
                  </div>
                  
                  <div className={`w-full md:w-1/2 mt-4 md:mt-0 ${index % 2 === 0 ? 'md:order-2 md:pl-12' : 'md:pr-12'}`}>
                    <div className={`h-48 rounded-lg overflow-hidden transition-all duration-300 ${
                      activeStep === index ? 'shadow-2xl ring-2 ring-red-500/50' : ''
                    }`}>
                      <img 
                        src={step.image} 
                        alt={step.title} 
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Digital Verification Section */}
        <div className="mb-16 initially-hidden opacity-0">
          <h2 className="text-3xl font-bold mb-8 text-center">Digital Verification Process</h2>
          
          <div className="grid grid-cols-1 gap-8">
            <div className="bg-gradient-to-br from-red-600/20 to-pink-600/20 rounded-2xl overflow-hidden shadow-xl border border-red-500/30 transform transition-all duration-500 hover:scale-[1.02]">
              <div className="p-8">
                <div className="flex items-center mb-6">
                  <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mr-4">
                    <Database className="w-8 h-8 text-red-300" />
                  </div>
                  <h3 className="text-2xl font-bold">Digital Verification</h3>
                </div>
                
                <div className="space-y-6">
                  <div className="flex items-start">
                    <div className="bg-red-500/20 rounded-full p-1 mr-3 mt-1">
                      <CheckCircle className="h-4 w-4 text-red-300" />
                    </div>
                    <div>
                      <h4 className="font-bold text-lg">AI-Powered Detection</h4>
                      <p className="text-white/80">Uses artificial intelligence to identify fraudulent documents.</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="bg-red-500/20 rounded-full p-1 mr-3 mt-1">
                      <CheckCircle className="h-4 w-4 text-red-300" />
                    </div>
                    <div>
                      <h4 className="font-bold text-lg">Nationwide Records</h4>
                      <p className="text-white/80">Searches criminal records across India.</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="bg-red-500/20 rounded-full p-1 mr-3 mt-1">
                      <CheckCircle className="h-4 w-4 text-red-300" />
                    </div>
                    <div>
                      <h4 className="font-bold text-lg">Fast Results</h4>
                      <p className="text-white/80">Delivers verification results in 24-48 hours.</p>
                    </div>
                  </div>
                </div>
                
                <div className="mt-8 bg-red-900/30 p-4 rounded-lg">
                  <h4 className="font-bold mb-2">Benefits:</h4>
                  <ul className="grid grid-cols-2 gap-2">
                    <li className="flex items-center">
                      <span className="text-red-300 mr-2">✓</span>
                      <span>Quick Processing</span>
                    </li>
                    <li className="flex items-center">
                      <span className="text-red-300 mr-2">✓</span>
                      <span>High Accuracy</span>
                    </li>
                    <li className="flex items-center">
                      <span className="text-red-300 mr-2">✓</span>
                      <span>Enhanced Security</span>
                    </li>
                    <li className="flex items-center">
                      <span className="text-red-300 mr-2">✓</span>
                      <span>Cost Effective</span>
                    </li>
                  </ul>
                </div>
              </div>
              
              <div className="h-48 relative">
                <img 
                  src="https://images.pexels.com/photos/590016/pexels-photo-590016.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1" 
                  alt="Digital Verification" 
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-red-900/90 to-transparent flex items-end">
                  <p className="p-4 text-white font-medium">Efficient digital screening for workplace safety</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Statistics Section */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-16 initially-hidden opacity-0">
          {[
            {
              number: "85%",
              text: "of employers screen candidates",
              color: "from-red-600/20 to-red-400/20",
              border: "border-red-500/30"
            },
            {
              number: "10-15%",
              text: "hide criminal history",
              color: "from-pink-600/20 to-pink-400/20",
              border: "border-pink-500/30"
            },
            {
              number: "24-48",
              text: "hours for results",
              color: "from-orange-600/20 to-orange-400/20",
              border: "border-orange-500/30"
            },
            {
              number: "70%",
              text: "safer with screening",
              color: "from-yellow-600/20 to-yellow-400/20",
              border: "border-yellow-500/30"
            }
          ].map((stat, index) => (
            <div 
              key={index}
              className={`bg-gradient-to-br ${stat.color} rounded-xl p-6 shadow-xl ${stat.border} text-center`}
            >
              <div className="text-4xl font-bold text-white mb-2">{stat.number}</div>
              <p className="text-white/80">{stat.text}</p>
            </div>
          ))}
        </div>
        
        {/* Call to Action */}
        <div className="bg-gradient-to-r from-red-600/30 to-pink-600/30 rounded-2xl p-8 shadow-2xl border border-red-500/20 text-center initially-hidden opacity-0">
          <h2 className="text-3xl font-bold mb-4">Ready to Ensure Workplace Safety?</h2>
          <p className="text-xl text-white/80 max-w-2xl mx-auto mb-8">
            Protect your organization and maintain a secure environment with comprehensive criminal background checks.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <button className="bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-6 rounded-lg transition-all duration-300 transform hover:scale-105 flex items-center">
              <Lock className="w-5 h-5 mr-2" />
              Get Started
            </button>
            <button className="bg-transparent border-2 border-red-400 hover:bg-red-400/20 text-white font-bold py-3 px-6 rounded-lg transition-all duration-300">
              Learn More
            </button>
          </div>
        </div>
      </section>

      <style jsx>{`
        .animate-fadeIn {
          animation: fadeIn 1s ease forwards;
        }

        .animate-fadeInDown {
          animation: fadeInDown 1s ease forwards;
        }

        .animate-fadeInUp {
          animation: fadeInUp 1s ease forwards;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes fadeInDown {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default CriminalBackgroundCheck;